import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { HeroSection } from "@/components/home/HeroSection";
import { NewsSection } from "@/components/home/NewsSection";
import { WindowSponsorshipTeaser } from "@/components/sponsors/WindowSponsorshipTeaser";
import { lazy, Suspense } from "react";

// Abschnitte unterhalb des sichtbaren Bereichs werden nachgeladen (kürzere kritische Kette)
const FeaturesSection = lazy(() => import("@/components/home/FeaturesSection").then((m) => ({ default: m.FeaturesSection })));
const SponsorsSection = lazy(() => import("@/components/home/SponsorsSection").then((m) => ({ default: m.SponsorsSection })));
const FAQSection = lazy(() => import("@/components/home/FAQSection").then((m) => ({ default: m.FAQSection })));
const CTASection = lazy(() => import("@/components/home/CTASection").then((m) => ({ default: m.CTASection })));
const HowWeWorkSection = lazy(() => import("@/components/home/HowWeWorkSection").then((m) => ({ default: m.HowWeWorkSection })));
const CommunityVotingTeaser = lazy(() => import("@/components/home/CommunityVotingTeaser").then((m) => ({ default: m.CommunityVotingTeaser })));
const GoogleReviewsSection = lazy(() => import("@/components/home/GoogleReviewsSection").then((m) => ({ default: m.GoogleReviewsSection })));
const LocationMapSection = lazy(() => import("@/components/home/LocationMapSection").then((m) => ({ default: m.LocationMapSection })));
const Hof1Section = lazy(() => import("@/components/home/Hof1Section").then((m) => ({ default: m.Hof1Section })));
import { SEO } from "@/components/SEO";
import { CheckCircle, X } from "lucide-react";

const homepageSchema = [
  {
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Was ist der Kultmans e.V. in Döbeln?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Kultmans e.V. ist ein ehrenamtlich geführter Kultur- und Begegnungsort im Alten Stemke Werk in Döbeln, Sachsen. Der Verein organisiert Musikveranstaltungen, Sportkurse, KI-Workshops und Netzwerktreffen, um Bürger unterschiedlicher Altersgruppen zusammenzubringen."
        }
      },
      {
        "@type": "Question",
        "name": "Ist Kultmans politisch?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Nein. Kultmans e.V. ist ein gemeinnütziger Verein ohne politischen oder ideologischen Hintergrund. Der Verein versteht sich als offener Begegnungsort für alle Menschen, unabhängig von politischer Überzeugung, Herkunft oder Alter."
        }
      },
      {
        "@type": "Question",
        "name": "Wie finanziert sich der Kultmans e.V.?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Kultmans e.V. finanziert sich über Mitgliedsbeiträge, Sponsoring und Unterstützung aus der Region. Alle Einnahmen fließen direkt in Infrastruktur, Veranstaltungen und neue Projekte."
        }
      },
      {
        "@type": "Question",
        "name": "Was sind die Ziele des Kultmans e.V.?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Kultmans e.V. stärkt den sozialen Zusammenhalt in Döbeln und Mittelsachsen, belebt ein historisches Industriegebäude nachhaltig, schafft Perspektiven für Jugendliche und Erwachsene und vernetzt Unternehmen, Handwerker und Kreative in der Region."
        }
      },
      {
        "@type": "Question",
        "name": "Wer leitet den Kultmans e.V.?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Der Verein wird ehrenamtlich geleitet von Rico Kretschel (1. Vorsitzender), Michael Gunkel (2. Vorsitzender), Roger Glanz (3. Vorsitzender, Bereich Bauwesen), Michael Lau (Schatzmeister) und Katrin Kressler-Schmidt (Vorstand Kommunikation). Aktuell zählt der Verein über 38 aktive Mitglieder."
        }
      },
      {
        "@type": "Question",
        "name": "Wie kann ich Mitglied bei Kultmans e.V. werden?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Eine Mitgliedschaft kann online über die Webseite beantragt werden. Der monatliche Beitrag beträgt 15 € für Einzelpersonen. Als Mitglied erhalten Sie Zugang zu allen Vereinsbereichen, Workshops und Events sowie Mitbestimmung bei Vereinsprojekten."
        }
      },
      {
        "@type": "Question",
        "name": "Kann ich Räume im Kultmans mieten?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja. Das Alte Stemke Werk bietet ca. 500 m² Eventfläche mit bis zu 200 Personen Kapazität. Die Räume eignen sich für Firmenveranstaltungen, Konzerte, Tagungen und private Feiern. Kontaktieren Sie uns für ein individuelles Angebot."
        }
      },
      {
        "@type": "Question",
        "name": "Wie kann mein Unternehmen den Kultmans e.V. unterstützen?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Unternehmen können den Verein als Sponsor, Sachspender oder Förderer unterstützen. Sponsoren erhalten Sichtbarkeit auf der Website und bei Veranstaltungen, Networking-Zugang und steuerliche Vorteile. Kontaktieren Sie uns für ein individuelles Sponsoring-Paket."
        }
      }
    ]
  }
];

const Index = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (searchParams.get("newsletter") === "confirmed") {
      setShowConfirmation(true);
      // Clean up URL
      searchParams.delete("newsletter");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Kultmans e.V. Döbeln – Kultur, Ehrenamt & Gemeinschaft"
        description="Ehrenamtlich geführter Kultur- und Begegnungsort im Alten Stemke Werk in Döbeln. Musikevents, Sportkurse, Workshops und Netzwerktreffen."
        keywords="Kultmans, Kulturverein Döbeln, Eventlocation Sachsen, ehrenamtliches Engagement Mittelsachsen, Gemeinschaft Döbeln, Kultur Döbeln, soziokulturell, generationenübergreifend, nachhaltige Stadtentwicklung"
        canonicalPath="/"
        schema={homepageSchema}
      />
      <Header />

      {/* Newsletter DOI Bestätigung */}
      {showConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 text-center animate-in zoom-in-95 duration-300">
            <button
              onClick={() => setShowConfirmation(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Schließen"
            >
              <X size={20} />
            </button>
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={32} className="text-primary" />
            </div>
            <h2 className="font-display text-2xl font-semibold text-foreground mb-3">
              Newsletter bestätigt!
            </h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Vielen Dank! Deine E-Mail-Adresse wurde erfolgreich bestätigt. Du erhältst ab sofort unseren Newsletter mit den neuesten Infos zu Events, Kursen und allem rund um Kultmans.
            </p>
            <button
              onClick={() => setShowConfirmation(false)}
              className="inline-flex items-center justify-center rounded-lg bg-primary text-primary-foreground px-6 py-2.5 text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Weiter zur Webseite
            </button>
          </div>
        </div>
      )}

      <main id="hauptinhalt">
        <HeroSection />
        <NewsSection />
        <WindowSponsorshipTeaser />
        <Suspense fallback={<div className="min-h-[40vh]" />}>
          <FeaturesSection />
          <Hof1Section />
          <HowWeWorkSection />
          <CommunityVotingTeaser />
          <SponsorsSection />
          <GoogleReviewsSection />
          <FAQSection />
          <LocationMapSection />
          <CTASection />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
